import { useState } from "react";

import { Icon, Text, Button, Dialog } from "@ui-component-library";
import { useSubscription } from "../../../context/SubscriptionCheckoutContext";
import { formatPriceWithCurrency } from "../../../utils";

const getPaymentLabels = (paymentMethod) => {
  if (paymentMethod === "klarna") {
    return {
      subtitle: "Vi skickar en bekräftelse till din e-post inom kort",
      email:
        "Vi skickar en orderbekräftelse till din e-post. Kontrollera din skräppost om du inte hittar den.",
    };
  }
  return {
    subtitle: "Vi kontaktar dig inom kort",
    contact:
      "Eftersom din fastighet är ovanligt stor vill vi ge dig en extra noggrann och personlig service",
  };
};

export default function ThankYou() {
  const { selectedProperties, orderPrices, couponDiscount, paymentMethod } =
    useSubscription();

  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);

  const totalForestArea = selectedProperties.reduce(
    (acc, property) => acc + property.forestArea,
    0
  );
  const totalNumberOfProperties = selectedProperties.length;

  const hasSelectedProperties = selectedProperties.length > 0;

  const paymentLabels = getPaymentLabels(paymentMethod);

  return (
    <>
      <div className="flex flex-col gap-6 w-full">
        <div className="flex flex-col gap-2 items-center justify-center">
          <div className="flex flex-row gap-2 items-center">
            <Icon
              tw
              name="CheckOutlined"
              size="xlarge"
              className="fill-success-700"
            />
            <Text variant="text-lg/leading-none/medium">
              Tack för din beställning!
            </Text>
          </div>
          <Text
            variant="text-sm/leading-normal/normal"
            className="text-pebble-500"
          >
            {paymentLabels.subtitle}
          </Text>
        </div>
        <div className="w-full h-[0.5px] border border-stone-500/17" />
        <Text variant="text-base/leading-normal/medium">Sammanfattning</Text>
        <div className="w-full flex flex-col bg-white-50 rounded-xl p-4 gap-4">
          <div className="flex flex-row gap-2 justify-start items-start">
            <Icon
              tw
              name="MapPinSimple"
              size="large"
              spacing="medium"
              className="fill-brown-700 bg-stone-200"
            />
            <div className="flex flex-col flex-1 h-full justify-center gap-2">
              <Text variant="text-sm/leading-normal/medium">
                Fastighet ({totalNumberOfProperties})
              </Text>

              <div className="flex flex-col gap-2">
                {selectedProperties.map((property) => (
                  <Text
                    variant="text-sm/leading-normal/normal"
                    className="text-pebble-500"
                  >
                    {property.designation}
                  </Text>
                ))}
              </div>
            </div>
            <Text
              variant="text-sm/leading-normal/normal"
              className="text-cloud-900 mb-3"
            >
              {totalForestArea} ha
            </Text>
          </div>
          <Button
            tw
            variant="secondary"
            size="small"
            onClick={() => setIsOrderDetailsOpen(true)}
          >
            Visa detaljer
          </Button>
          {hasSelectedProperties && (
            <>
              <Text variant="text-sm/leading-normal/medium">Prisuppgifter</Text>
              <div className="flex flex-row gap-2 justify-between items-start">
                <Text variant="text-sm/leading-normal/normal">
                  Treebula Plus
                </Text>
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(orderPrices.propertiesTotalPrice)}
                </Text>
              </div>

              <div className="w-full h-[0.5px] border border-stone-500/17" />

              {orderPrices.MultiPlusDiscount && ( 
              <>
                <div className="flex flex-row gap-2 justify-start items-start">
                  <div className="flex flex-col gap-1">
                    <Text variant="text-sm/leading-normal/medium">
                      {orderPrices.MultiPlusDiscount.discountReason}
                    </Text>
                    <Text
                      variant="text-sm/leading-normal/normal"
                      className="text-pebble-500"
                    >
                      - {formatPriceWithCurrency(orderPrices.MultiPlusDiscount.discountAmount)}
                    </Text>
                  </div>
                </div>
                <div className="w-full h-[0.5px] border border-stone-500/17" />
              </>
              )}

              <div className="flex flex-row gap-2 justify-between items-center">
                <div className="flex flex-row gap-2">
                  <Text variant="text-sm/leading-normal/medium">Totalt</Text>
                  <Text
                    variant="text-sm/leading-normal/normal"
                    className="text-charcoal-300"
                  >
                    Inkl. moms
                  </Text>
                </div>

                <div className="flex flex-row gap-1">
                  <Text variant="text-sm/leading-normal/medium">
                    {formatPriceWithCurrency(
                      orderPrices.discountPrice + orderPrices.tax
                    )}
                  </Text>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="w-full h-[0.5px] border border-stone-500/17" />
        <Text variant="text-base/leading-normal/medium">Betalningsmetod</Text>

        <img
          alt="Klarna logo"
          width="60px"
          height="24px"
          src={`${Window.static_path}img/payment-provider/klarna.png`}
        />

        <div className="w-full h-[0.5px] border border-stone-500/17" />

        <Text variant="text-base/leading-normal/medium">Vad händer nu?</Text>

        <div className="w-full flex flex-col bg-white-50 rounded-xl p-4 gap-4">
          {paymentLabels.contact && (
            <div className="flex flex-row gap-4 items-start">
              <Icon tw name="PhoneCallSimple" size="large" className="" />
              <div className="flex flex-col gap-1">
                <Text variant="text-base/leading-normal/medium">
                  Vi kontaktar dig
                </Text>
                <Text
                  variant="text-sm/leading-normal/normal"
                  className="text-pebble-500"
                >
                  {paymentLabels.contact}
                </Text>
              </div>
            </div>
          )}
          {paymentLabels.email && (
            <div className="flex flex-row gap-4 items-start">
              <Icon tw name="MailSimple" size="large" className="" />
              <div className="flex flex-col gap-1">
                <Text variant="text-base/leading-normal/medium">
                  Orderbekräftelse
                </Text>
                <Text
                  variant="text-sm/leading-normal/normal"
                  className="text-pebble-500"
                >
                  {paymentLabels.email}
                </Text>
              </div>
            </div>
          )}
        </div>

        <div className="w-full h-[0.5px] border border-stone-500/17" />

        <Text variant="text-base/leading-normal/medium">Behöver du hjälp?</Text>

        <div className="w-full flex flex-col bg-white-50 rounded-xl overflow-hidden">
          <a
            href="mailto:info@treebula.se"
            className="flex flex-row gap-4 p-4 items-center justify-start hover:bg-white-500"
          >
            <Icon tw name="MailSimple" size="large" className="" />
            <div className="flex flex-col gap-1 flex-1">
              <Text variant="text-base/leading-normal/medium">E-post</Text>
              <Text
                variant="text-sm/leading-normal/normal"
                className="text-pebble-500"
              >
                info@treebula.se
              </Text>
            </div>
            <Icon
              tw
              name="ChevronDownSimple"
              size="large"
              className="-rotate-90"
            />
          </a>

          <div className="w-full h-[0.5px] border border-stone-500/17" />

          <a
            className="flex flex-row gap-4 p-4 items-center justify-start hover:bg-white-500"
            href="tel:08339944"
          >
            <Icon tw name="CustomerSupportSimple" size="large" className="" />
            <div className="flex flex-col gap-1 flex-1">
              <Text variant="text-base/leading-normal/medium">Ring</Text>
              <Text
                variant="text-sm/leading-normal/normal"
                className="text-pebble-500"
              >
                08 - 33 99 44
              </Text>
            </div>
            <Icon
              tw
              name="ChevronDownSimple"
              size="large"
              className="-rotate-90"
            />
          </a>
        </div>
      </div>

      {/* Order Details Dialog */}
      <Dialog isOpen={isOrderDetailsOpen} size="small">
        <Dialog.Header>
          <div className="flex w-full justify-between">
            <Text variant="text-base/leading-normal/medium">Prisdetaljer</Text>
            <Button
              tw
              variant="secondary"
              size="icon"
              onClick={() => setIsOrderDetailsOpen(false)}
            >
              <Icon
                tw
                name="CrossSimple"
                className="color-charcoal-500"
                size="medium"
              />
            </Button>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          <div className="flex flex-col gap-4 h-full">
            <div className="flex flex-col gap-4">
              <div className="flex flex-row gap-2 justify-between items-center">
                <Text variant="text-sm/leading-normal/normal">
                  Treebula Plus
                </Text>
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(orderPrices.propertiesTotalPrice)}
                </Text>
              </div>

              <div className="w-full h-[0.5px] border border-stone-500/17" />

              {couponDiscount && (
                <div className="flex flex-row gap-2 justify-between items-center">
                  <Text variant="text-sm/leading-normal/normal">
                    Rabattkod {couponDiscount.coupon_code}
                  </Text>
                  <Text variant="text-sm/leading-normal/medium">
                    -
                    {formatPriceWithCurrency(
                      couponDiscount.coupon_discount_price
                    )}
                  </Text>
                </div>
              )}
              <div className="flex flex-row gap-2 justify-between items-center">
                <Text
                  variant="text-sm/leading-normal/normal"
                  className="text-charcoal-300"
                >
                  Moms
                </Text>
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(orderPrices.tax)}
                </Text>
              </div>
              <div className="flex flex-row gap-2 justify-between items-center">
                <Text
                  variant="text-sm/leading-normal/normal"
                  className="text-charcoal-300"
                >
                  Inkl. moms
                </Text>
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(
                    orderPrices.discountPrice + orderPrices.tax
                  )}
                </Text>
              </div>
            </div>

            <div className="w-full h-[0.5px] border border-stone-500/17 mt-auto" />

            <div className="flex flex-row gap-2 justify-between items-center">
              <Text variant="text-sm/leading-normal/medium">Exkl. moms</Text>
              <div className="flex flex-col gap-1 items-end">
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(orderPrices.discountPrice)}
                </Text>
                <Text
                  variant="text-sm/leading-normal/normal"
                  className="text-charcoal-300"
                >
                </Text>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  );
}
